import { Command } from 'commander';
import { login } from '../auth.js';
import { loadConfig, saveConfig } from '../config.js';
import {
  exchangeCodeForTokens,
  expiresInToIso,
  refreshTokenMissingWarning,
  startCallbackServer,
  getAuthKitDomain,
  getAuthKitClientId,
  OAUTH_CALLBACK_PORT,
} from '../oauth.js';
import {
  generateCodeVerifier,
  generateCodeChallenge,
  generateState,
} from '../pkce.js';
import { setOAuthTokens } from '../token-store.js';
import { isSSH } from '../env.js';
import { nudgeReportActions } from '../report-inbox.js';

const FALLBACK_URL_DELAY_MS = 5_000;

export const loginCommand = new Command('login')
  .description('Authenticate with Rekor')
  .option('--token <token>', 'API key for headless/CI authentication (rec_...)')
  .option('--api-url <url>', 'API base URL')
  .action(async (opts: { token?: string; apiUrl?: string }) => {
    if (opts.token) {
      await login(opts.token, opts.apiUrl);
      console.log('Authenticated successfully');
      // Best-effort: remind the reporter of any reports awaiting their accept/contest.
      await nudgeReportActions();
      return;
    }

    // Let failures propagate to the top-level handler so the error prints cleanly and
    // telemetry flushes (a local process.exit here would bypass both).
    await browserLoginPkce(opts.apiUrl);
    console.log('Authenticated successfully');
    // Best-effort: remind the reporter of any reports awaiting their accept/contest.
    await nudgeReportActions();
  });

async function browserLoginPkce(apiUrl?: string): Promise<void> {
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = generateCodeChallenge(codeVerifier);
  const state = generateState();

  const port = OAUTH_CALLBACK_PORT;
  const redirectUri = `http://127.0.0.1:${port}/callback`;

  const authkitDomain = getAuthKitDomain();
  const clientId = getAuthKitClientId();

  const authorizeUrl = new URL(`${authkitDomain}/oauth2/authorize`);
  authorizeUrl.searchParams.set('client_id', clientId);
  authorizeUrl.searchParams.set('redirect_uri', redirectUri);
  authorizeUrl.searchParams.set('response_type', 'code');
  authorizeUrl.searchParams.set('code_challenge', codeChallenge);
  authorizeUrl.searchParams.set('code_challenge_method', 'S256');
  authorizeUrl.searchParams.set('state', state);
  // offline_access asks AuthKit to issue a refresh_token alongside the access_token —
  // without it the CLI would re-prompt every ~1h when the access token expires.
  authorizeUrl.searchParams.set('scope', 'openid profile email offline_access');

  // Start the callback server first so the redirect can land.
  const callbackPromise = startCallbackServer(port, state);

  let fallbackTimer: ReturnType<typeof setTimeout> | undefined;
  if (isSSH()) {
    // No usable browser over SSH — print the URL up front instead of auto-opening.
    console.log('SSH session detected. Open this URL in a browser to authenticate:');
    console.log(authorizeUrl.toString());
  } else {
    console.log('Opening browser for authentication...');
    const open = await import('open').then(m => m.default);

    // open() resolves on spawn, not browser launch — print a fallback URL after a delay.
    let fallbackPrinted = false;
    fallbackTimer = setTimeout(() => {
      fallbackPrinted = true;
      console.log(`If the browser didn't open, visit: ${authorizeUrl.toString()}`);
    }, FALLBACK_URL_DELAY_MS);

    open(authorizeUrl.toString()).catch(() => {
      if (fallbackPrinted) return;
      clearTimeout(fallbackTimer);
      console.log('Could not open browser automatically.');
      console.log(`Visit: ${authorizeUrl.toString()}`);
    });
  }

  let code: string;
  try {
    ({ code } = await callbackPromise);
  } finally {
    if (fallbackTimer) clearTimeout(fallbackTimer);
  }

  const tokens = await exchangeCodeForTokens(authkitDomain, clientId, code, codeVerifier, redirectUri);
  const expiresAt = expiresInToIso(tokens.expires_in);
  await setOAuthTokens(tokens.access_token, tokens.refresh_token, expiresAt);

  const noRefreshWarning = refreshTokenMissingWarning(tokens.refresh_token);
  if (noRefreshWarning) console.warn(noRefreshWarning);

  const config = loadConfig();
  saveConfig({
    ...config,
    api_url: apiUrl ?? config.api_url,
  });
}
